import { AxiosPromise, AxiosResponse, AxiosError } from "axios";
import { ApiService } from "./api.service";
import {
  Tool,
  ToolCall,
  ToolCallRequest,
  ToolCallResponse,
  ToolRequest,
  ToolResponse,
  ToolTemplateResponse,
  UpdateToolRequest,
  parseToolCallResponse,
  parseToolResponse,
} from "@/types/app/tool";
import useSWR, { SWRConfiguration } from "swr";
import { getAuthToken } from "@/lib/use-auth";

export const upsertTool = async (request: ToolRequest): AxiosPromise<Tool> => {
  const response: AxiosResponse<ToolResponse> = await ApiService.post(`/v5/tools`, request);
  return { ...response, data: parseToolResponse(response.data) };
};

export const moveTool = async (toolId: string, update: UpdateToolRequest): AxiosPromise<Tool> => {
  const response: AxiosResponse<ToolResponse> = await ApiService.patch(`/v5/tools/${toolId}`, update);
  return { ...response, data: parseToolResponse(response.data) };
};

export const useToolTemplates = (swrOptions: SWRConfiguration<ToolTemplateResponse[]> = {}) => {
  const { data, error, mutate } = useSWR<ToolTemplateResponse[]>([`/v5/tools/templates`, getAuthToken()], swrOptions);
  return { toolTemplates: data, error: error, loading: !data && !error, mutate };
};

export type CallToolOutput =
  | {
      status_code: 200;
      output: string | null;
    }
  | {
      status_code: 200;
      error: string;
    }
  | {
      status_code: 422;
      error: string;
    };

export const callTool = async (request: ToolCallRequest): Promise<CallToolOutput> => {
  try {
    const response: AxiosResponse<ToolCallResponse> = await ApiService.post(`/v5/tools/call`, request);
    const data = parseToolCallResponse(response.data);
    if (data.error) {
      return { status_code: 200, error: data.error };
    }
    return { status_code: 200, output: data.output || "" };
  } catch (err: any) {
    if (err instanceof AxiosError && err.response?.status === 422) {
      // Provided inputs do not match the tool's inferred JSON schema
      return { status_code: 422, error: JSON.stringify(err.response?.data["detail"], null, 2) };
    }
    throw err;
  }
};
